import { type ProviderConfig, useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import murmurhash from 'murmurhash';
import { useEffect, useRef, useState } from 'react';

import appLogger from '../logging/appLogger';
import type User from '../types/user';

const launchDarklyLogger = {
    debug: (message: string) => {
        appLogger.logTrace({
            message: message,
            properties: {
                source: 'Launch Darkly',
            },
        });
    },
    info: (message: string) => {
        appLogger.logTrace({
            message: message,
            properties: {
                source: 'Launch Darkly',
            },
        });
    },
    warn: (message: string) => {
        appLogger.logEvent({
            name: message,
            properties: {
                source: 'Launch Darkly',
            },
        });
    },
    error: (message: string) => {
        appLogger.logException({
            error: new Error(message),
            properties: {
                source: 'Launch Darkly',
            },
        });
    },
};
const ldOptions: ProviderConfig = {
    clientSideID: process.env.LAUNCH_DARKLY_CLIENTID,
    reactOptions: {
        useCamelCaseFlagKeys: false,
    },
    options: {
        // Use this option to provide values for a feature flags that isn't the default JS/TS value.
        bootstrap: {
            'mfe-uat-access': true,
        },
        logger: launchDarklyLogger,
    },
};

interface FeatureFlagProviderProps {
    user: User;
    wrappedComponent: React.ComponentType;
}

function withLDReadinessCheck<T>(WrappedComponent: React.ComponentType<T>): React.ComponentType<T> {
    return function LDReadinessCheckedComponent(props: T) {
        const [isLDReady, setLDReady] = useState(false);
        const ldClient = useLDClient();

        const suspended = useRef<Promise<void>>(
            ldClient?.waitUntilReady().then(() => {
                setLDReady(true);
            }) || Promise.resolve()
        );

        useEffect(() => {
            const checkLDClientReadiness = async () => {
                if (ldClient && !isLDReady) {
                    await suspended.current;
                    setLDReady(true);
                }
            };

            checkLDClientReadiness();
        }, [ldClient, isLDReady]);

        if (!isLDReady) {
            throw suspended.current;
        }

        return <WrappedComponent {...props} />;
    };
}

// This function hashes the GUID (ciamId) and converts it to a number between 0-99.
// This allows us to target users 50/50 on the feature flag tool.
export const getUserBucket = (ciamId: string) => {
    if (!ciamId) return null;
    const hashValue = murmurhash.v3(ciamId) >>> 0; // Converts to unsigned 32-bit integer
    return hashValue % 100;
};

export const FeatureFlagProvider = ({ user, wrappedComponent }: FeatureFlagProviderProps) => {
    // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
    let userContext;

    if (user) {
        const bucket = getUserBucket(user.consumerResourceId);

        userContext = {
            kind: 'user',
            key: `user-key-${user.consumerResourceId}`,
            email: user.username,
            username: user.username,
            consumerResourceId: user.consumerResourceId,
            createTime: user.createTime,
            bucket,
        };
    } else {
        userContext = {
            kind: 'user',
            anonymous: true,
        };
    }

    const completedContext = {
        ...ldOptions,
        context: userContext,
    };

    const LDComponent = withLDProvider(completedContext)(wrappedComponent);

    return <LDComponent />;
};

function withFeatureFlag<T>(): (
    WrappedComponent: React.ComponentType<T>
) => React.ComponentType<T> {
    return function HoistedComponent(
        WrappedComponent: React.ComponentType<T>
    ): React.ComponentType<T> {
        const result = withLDProvider(ldOptions)(
            withLDReadinessCheck(WrappedComponent)
        ) as React.ComponentType<T>;

        return result;
    };
}

export default withFeatureFlag;
